import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import StoreHeader from "./Header";
import CartDrawer from "./CartDrawer";

const STORE_ROUTES = ["/store", "/checkout", "/account"] as const;

const MESSAGE_TYPE = "modelerman-store-overlay";

const StoreAccessToggle: React.FC = () => {
    const location = useLocation();

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const overlayMode = searchParams.get("storeOverlay");
    const isEmbedMode = overlayMode === "embed";
    const autoOpenOverlay = overlayMode === "open" || isEmbedMode;
    const isHomeRoute = location.pathname === "/";
    const hideTrigger = isHomeRoute || isEmbedMode || overlayMode === "hidden";

    const [isActive, setIsActive] = useState(autoOpenOverlay);

    const isStoreRoute = useMemo(
        () => STORE_ROUTES.some((prefix) => location.pathname.startsWith(prefix)),
        [location.pathname]
    );

    useEffect(() => {
        if (isStoreRoute) {
            setIsActive(false);
        }
    }, [isStoreRoute]);

    useEffect(() => {
        if (autoOpenOverlay) {
            setIsActive(true);
        }
    }, [autoOpenOverlay]);

    useEffect(() => {
        const body = document.body;
        if (isActive) {
            body.classList.add("overflow-hidden");
        } else {
            body.classList.remove("overflow-hidden");
        }

        return () => body.classList.remove("overflow-hidden");
    }, [isActive]);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            const data = event.data as { type?: string; command?: "open" | "close" | "toggle" };
            if (!data || data.type !== MESSAGE_TYPE) {
                return;
            }

            if (data.command === "open") {
                setIsActive(true);
            } else if (data.command === "close") {
                setIsActive(false);
            } else if (data.command === "toggle") {
                setIsActive((prev) => !prev);
            }
        };

        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []);

    useEffect(() => {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: MESSAGE_TYPE, active: isActive }, window.location.origin);
        }
    }, [isActive]);

    if (isStoreRoute) {
        return null;
    }

    return (
        <>
            {!hideTrigger && (
                <button
                    type="button"
                    onClick={() => setIsActive((prev) => !prev)}
                    className="fixed bottom-6 right-6 z-40 flex flex-col rounded-2xl border border-white/10 bg-midnight-800/90 px-5 py-3 text-left text-white shadow-drawer transition hover:bg-midnight-700/90"
                >
                    <span className="text-sm font-semibold tracking-wide">
                        {isActive ? "Hide store controls" : "Modelerman Store"}
                    </span>
                    <span className="text-xs text-white/60">
                        {isActive ? "Tap to return" : "Account & cart access"}
                    </span>
                </button>
            )}

            {isActive && (
                <>
                    <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setIsActive(false)} />
                    <StoreHeader variant="overlay" onRequestClose={() => setIsActive(false)} />
                    <CartDrawer />
                </>
            )}
        </>
    );
};

export default StoreAccessToggle;
